import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { Button, type ButtonSize, type ButtonVariant } from '@/shared/ui/button';
import { DropdownMenu } from '@/shared/ui/dropdown-menu';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export interface SplitButtonProps {
  /** Popisek hlavní akce. */
  children: ReactNode;
  /** Hlavní akce (levá část). */
  onClick: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: PhxIconName;
  /** Běží hlavní akce – spinner vlevo, obě části zablokované. */
  loading?: boolean;
  /** Zablokuje obě části. */
  disabled?: boolean;
  /**
   * Obsah kontextového menu (`MenuItem` / `MenuDivider` / `MenuLabel`). Když chybí, vykreslí se
   * PROSTÉ tlačítko bez šipky – volající tak nemusí větvit mezi Button a SplitButton.
   */
  menu?: ReactNode;
  /** Zablokuje jen šipku; hlavní akce zůstane aktivní. */
  menuDisabled?: boolean;
  /** Přístupný název šipky. Default z `sharedUi:splitButton.more`. */
  menuAriaLabel?: string;
  menuAlign?: 'start' | 'end';
  /**
   * Směr otevření menu. `DropdownMenu` neumí auto-flip, takže v patičce připnuté ke spodní hraně
   * okna je nutné předat `top`, jinak menu vyjede mimo viewport.
   */
  menuPlacement?: 'bottom' | 'top';
  /** Neviditelný backdrop – nutný na stránkách s legacy iframy. */
  menuBackdrop?: boolean;
  /** Třídy na wrapperu (ne na tlačítkách). */
  className?: string;
  /** Minimální šířka menu; default 280px kvůli delším českým popiskům. */
  menuClassName?: string;
  type?: 'button' | 'submit';
  'data-testid'?: string;
}

// `focus-visible:z-10` + `relative`: bez toho by překrývající se sousední tlačítko schovalo
// focus ring (shadow-phx-focus) té zaostřené části.
const MAIN_PART = 'relative rounded-r-none focus-visible:z-10';

const ARROW_PART = [
  'relative rounded-l-none -ml-px focus-visible:z-10',
  // Šipka běží na STEJNÉ `size` jako hlavní část (jinak by se výšky rozešly – `icon` má pevné
  // `h-phx-control`, kdežto `lg` `h-phx-control-lg`). Zbývá jen zúžit vodorovný padding.
  // `!` tu být nemusí: `cn()` má phx spacing škálu zaregistrovanou v tailwind-merge,
  // takže `px-phx-2` korektně přebije `px-phx-5` z velikosti.
  'px-phx-2',
  // Oddělovač přes `bg-current` dědí barvu textu tlačítka, takže funguje pro všechny varianty
  // i pro light/dark bez dalšího tokenu.
  'before:pointer-events-none before:absolute before:inset-y-[18%] before:left-0',
  'before:w-px before:bg-current before:opacity-30',
].join(' ');

/**
 * Tlačítko s hlavní akcí a šipkou, která otevře kontextové menu s alternativami téže akce.
 *
 * Kompozice existujících primitiv ({@link Button} + {@link DropdownMenu}) – žádné vlastní
 * stylování od nuly. Bez `menu` se degraduje na obyčejný `Button`.
 */
export function SplitButton({
  children,
  onClick,
  variant,
  size,
  iconLeft,
  loading,
  disabled,
  menu,
  menuDisabled,
  menuAriaLabel,
  menuAlign = 'end',
  menuPlacement = 'bottom',
  menuBackdrop = true,
  className,
  menuClassName,
  type,
  'data-testid': dataTestId,
}: SplitButtonProps) {
  const { t } = useTranslation('sharedUi');
  const [open, setOpen] = useState(false);

  const mainButton = (
    <Button
      type={type}
      variant={variant}
      size={size}
      iconLeft={iconLeft}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      data-testid={dataTestId}
      className={menu ? MAIN_PART : className}
      // Šipka dolů na hlavní části otevře menu (běžná konvence split buttonu).
      onKeyDown={
        menu && !menuDisabled
          ? (event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setOpen(true);
              }
            }
          : undefined
      }
    >
      {children}
    </Button>
  );

  if (!menu) return mainButton;

  return (
    <div role="group" className={cn('inline-flex items-stretch', className)}>
      {mainButton}
      <DropdownMenu
        open={open}
        onOpenChange={setOpen}
        align={menuAlign}
        placement={menuPlacement}
        backdrop={menuBackdrop}
        className={cn('min-w-[280px]', menuClassName)}
        trigger={
          <Button
            type="button"
            variant={variant}
            size={size}
            aria-label={menuAriaLabel ?? t('splitButton.more')}
            disabled={disabled || loading || menuDisabled}
            className={ARROW_PART}
          >
            <Icon name="chevdown" size={size === 'sm' ? 12 : 14} />
          </Button>
        }
      >
        {menu}
      </DropdownMenu>
    </div>
  );
}
